import { BIIconButton } from '@pulsepointinc/bi-components';
import { type FC, useMemo, useState } from 'react';
import type { ICustomFilter } from '../../../../../../helpers/helpers';
import { useFilters } from '../../../../../../hooks/useFilters';
import { useNpiListsMetadataApi } from '../../../../../../hooks/useWorkspaces';
import { useSubGroupFilterState } from '../../../hooks/useSubGroupFilterState';
import { useUserData } from '../../../hooks/useUserData';
import { GroupedIncludeExcludeFilter } from '../IncludeExcludeFilter';
import { StyledAnchor } from './styles';

interface GroupedData {
  awaiting: Record<string, { listName: string; listAlias?: string; url?: string }[]>;
  regular: Record<string, { listName: string; listAlias?: string; url?: string }[]>;
}

const formatGroupName = (name: string): string => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const NpiListName: FC<ICustomFilter> = ({ onChange }) => {
  const { userData } = useUserData();
  const [searchPhrase, setSearchPhrase] = useState<string>('');

  const { filters } = useFilters();
  const selectedAdvertiserID = filters?.pseudo_advertiser_id_filter?.expression;

  const { handleChange } = useSubGroupFilterState('npi_list_name_filter', onChange);

  const { listMetadata, isListMetadataLoading } = useNpiListsMetadataApi({
    accountID: Number(userData.acctId),
    advertiserID: Number(selectedAdvertiserID),
    limit: 1000,
    searchPhrase,
  });

  const groupedData: GroupedData = useMemo(() => {
    const result: GroupedData = {
      awaiting: {},
      regular: {},
    };

    const allGroups = listMetadata.reduce<Record<string, { listName: string; listAlias?: string; url?: string }[]>>(
      (acc, item) => {
        const formattedType = formatGroupName(item.listType);
        if (!acc[formattedType]) {
          acc[formattedType] = [];
        }
        acc[formattedType].push({
          listName: item.listName,
          listAlias: item.listAlias,
          url: item.url,
        });
        return acc;
      },
      {},
    );

    Object.entries(allGroups).forEach(([key, value]) => {
      if (key.toLowerCase().startsWith('awaiting')) {
        result.awaiting[key] = value;
      } else {
        result.regular[key] = value;
      }
    });

    return result;
  }, [listMetadata]);

  return (
    <GroupedIncludeExcludeFilter
      filterName="npi_list_name_filter"
      label="NPI Lists"
      onMultiFilterChange={handleChange}
      groupedSuggestions={groupedData.regular}
      awaitingData={groupedData.awaiting}
      onSearchPhraseChange={setSearchPhrase}
      loading={isListMetadataLoading}
      urlAnchor={(category: string) => {
        const item = listMetadata.find((i) => i.listName === category);
        return (
          item?.url && (
            <StyledAnchor href={item.url} target="_blank" rel="noopener noreferrer">
              <BIIconButton icon="LinkExternal" />
            </StyledAnchor>
          )
        );
      }}
    />
  );
};
